import type ViewType from "~/@types/viewType";
import type { ContinuationTarget } from "~/helpers/pendingIntentLifecycle";
import type { ReceiverIdentityAdapter } from "~produce/production/helpers/continuationAdapter";

import { ReplenishmentRuleSummaryType } from "~/gql/graphqlBase.generated";
import isRecord from "~/helpers/isRecord";
import { RESTOCK_ITEM_TARGET_KIND } from "~restock/data-management/items/helpers/restockItemReceiverIdentity";
import { RESTOCK_STORE_TARGET_KIND } from "~restock/data-management/locations/helpers/restockStoreReceiverIdentity";

export const RESTOCK_RULE_SOURCE_KIND = "restock-rule";

export const RESTOCK_RULE_TARGET_KIND = "restockRule";

export const RESTOCK_RULES_DOCK_PAGE_ID = "data-management-rules";

export const RULES_PATH = "/restock/data-management/rules";

export type RestockRuleTab = "details" | "items" | "locations";

export type RestockRuleHopOrigin = "detailsTab" | "itemsTab" | "locationsTab" | "modal";

export type RuleInspectionOrigin = RestockRuleHopOrigin;

export const RULE_TAB_INDEX: Record<RestockRuleTab, number> = {
	details: 0,
	items: 1,
	locations: 2
};

export interface RestockRuleInspectionCheckpoint {
	origin: RestockRuleHopOrigin;
	ruleId: string;
	ruleName: null | string;
	ruleType: ReplenishmentRuleSummaryType;
	searchText?: string;
	targetTab?: "items" | "locations";
	viewMode?: ViewType;
}

const isValidOrigin = (value: unknown): value is RestockRuleHopOrigin =>
	value === "detailsTab" || value === "itemsTab" || value === "locationsTab" || value === "modal";

const isValidRuleType = (value: unknown): value is ReplenishmentRuleSummaryType =>
	value === ReplenishmentRuleSummaryType.Blocking ||
	value === ReplenishmentRuleSummaryType.Promotion ||
	value === ReplenishmentRuleSummaryType.Unknown;

const isValidViewMode = (value: unknown): value is undefined | ViewType =>
	value === undefined || value === "card" || value === "grid";

const isValidTargetTab = (value: unknown): value is undefined | "items" | "locations" =>
	value === undefined || value === "items" || value === "locations";

export const isRestockRuleInspectionCheckpoint = (value: unknown): value is RestockRuleInspectionCheckpoint => {
	if (!isRecord(value)) return false;
	return (
		typeof value.ruleId === "string" &&
		value.ruleId.length > 0 &&
		(value.ruleName === null || typeof value.ruleName === "string") &&
		isValidOrigin(value.origin) &&
		isValidRuleType(value.ruleType) &&
		(value.searchText === undefined || typeof value.searchText === "string") &&
		isValidTargetTab(value.targetTab) &&
		isValidViewMode(value.viewMode)
	);
};

export const restockRuleEditorKey = (ruleId: string, origin: RestockRuleHopOrigin = "modal"): string =>
	`${RESTOCK_RULE_SOURCE_KIND}:${origin}:${ruleId}`;

export const buildRuleItemTarget = (itemId: string): ContinuationTarget => ({
	targetId: itemId,
	targetKind: RESTOCK_ITEM_TARGET_KIND
});

export const buildRuleStoreTarget = (locationId: string): ContinuationTarget => ({
	targetId: locationId,
	targetKind: RESTOCK_STORE_TARGET_KIND
});

export const buildRuleTabTarget = (ruleId: string, tab: "items" | "locations"): ContinuationTarget => ({
	listFilters: { tab },
	targetId: ruleId,
	targetKind: RESTOCK_RULE_TARGET_KIND
});

const RESTOCK_RULE_ROUTE_PATTERN = /^\/restock\/data-management\/rules\/([^/?#]+)$/;

export const matchRestockRuleReceiverRoute = (pathname: string): ContinuationTarget | null => {
	const match = RESTOCK_RULE_ROUTE_PATTERN.exec(pathname);
	if (!match) return null;
	try {
		const segment = decodeURIComponent(match[1]);
		return segment ? { targetId: segment, targetKind: RESTOCK_RULE_TARGET_KIND } : null;
	} catch {
		return null;
	}
};

export const restockRuleReceiverIdentityAdapter: ReceiverIdentityAdapter = {
	buildCleanPath: ({ targetId }) => `${RULES_PATH}/${encodeURIComponent(targetId)}`,
	matchesRoute: matchRestockRuleReceiverRoute
};

export const isRulesPath = (pathname: string): boolean =>
	pathname === RULES_PATH || pathname.startsWith(`${RULES_PATH}/`);

const ruleContinuation = {
	buildRuleItemTarget,
	buildRuleStoreTarget,
	buildRuleTabTarget,
	isRestockRuleInspectionCheckpoint,
	isRulesPath,
	matchRestockRuleReceiverRoute,
	RESTOCK_RULE_SOURCE_KIND,
	RESTOCK_RULE_TARGET_KIND,
	RESTOCK_RULES_DOCK_PAGE_ID,
	restockRuleEditorKey,
	restockRuleReceiverIdentityAdapter,
	RULE_TAB_INDEX,
	RULES_PATH
};

export default ruleContinuation;
